import type { ReadField } from '@ns/plugin-picker-shared';
import { unknownFilterField } from '@ns/plugin-picker-shared';

import { type FieldSpec, isPickerValue } from './field-spec';
import { kqlStringLiteral } from './kql-literal';
import type { MatchValue } from './match';
import { MatchAuthorError, renderMatch, resolveMatch } from './match';
import type { Answer, Selected } from './page';
import { selectedFrom } from './page';
import type { AzureResourceGroup } from './types';

/** What a template author may narrow or strike out the group list with. */
export interface ResourceGroupFilter {
  location?: MatchValue;
  tags?: Readonly<Record<string, MatchValue>>;
}

/** The `ui:options` this picker offers: exactly three keys. */
export interface ResourceGroupPickerUiOptions {
  exclude?: ResourceGroupFilter;
  filter?: ResourceGroupFilter;
  subscriptionRef?: string;
}

/** The keys `filter` and `exclude` offer, and no others. */
const RESOURCE_GROUP_FILTER_FIELDS: readonly string[] = ['location', 'tags'];

const RESOURCE_GROUP_HEAD = [
  'resourcecontainers',
  '| where type =~ "microsoft.resources/subscriptions/resourcegroups"',
].join('\n');

/** The fields of {@link AzureResourceGroup}, one per column the tail below projects. */
export const RESOURCE_GROUP_FIELDS = {
  id: 'string',
  location: 'string',
  name: 'string',
  tags: 'optional',
  type: 'string',
} as const satisfies FieldSpec<AzureResourceGroup>;

const RESOURCE_GROUP_TAIL = [
  '| project id, name, location, tags, type',
  '| order by tolower(name) asc // on the visible text, case-insensitive',
].join('\n');

/**
 * Builds the KQL for the resource-group picker.
 *
 * @remarks
 * The subscription is NOT part of this text. It travels as the `subscriptions`
 * argument of the call: a typed list rather than text pasted into a
 * query language, which is what avoids an escaping question altogether.
 *
 * `exclude` strikes out what `filter` left over, and it is not an allow-list
 * turned around: `tostring(tags["x"])` is empty when the tag is absent, and
 * empty is unequal to what is excluded, so a group that does not carry the tag
 * survives.
 *
 * @param readField - Reads a field an author referenced with `ref`. Its own
 *   parameter rather than a key of `options`, because a required key inside an
 *   optional object is no compile gate: `build()` would still type-check.
 * @param options - `filter` and `exclude` as the author wrote them.
 * @returns The full query text.
 */
export function buildResourceGroupQuery(
  readField: ReadField,
  options?: {
    exclude?: ResourceGroupFilter;
    filter?: ResourceGroupFilter;
  },
): string {
  const { exclude, filter } = options ?? {};

  assertOffered('filter', filter);
  assertOffered('exclude', exclude);

  const clauses = [
    ...clausesFor(filter, false, readField),
    ...clausesFor(exclude, true, readField),
  ];

  return [RESOURCE_GROUP_HEAD, ...clauses, RESOURCE_GROUP_TAIL].join('\n');
}

/**
 * Rejects a side that this picker cannot act on.
 *
 * @remarks
 * An unoffered key or a side that is not key-value pairs is knowable without
 * fetching anything, so it is reported rather than quietly ignored. The test is
 * what THIS picker offers, not what Azure knows: `state` exists at Azure and is
 * not a thing here.
 */
function assertOffered(
  name: 'exclude' | 'filter',
  side: ResourceGroupFilter | undefined,
): void {
  if (side === undefined) return;
  if (typeof side !== 'object' || Array.isArray(side)) {
    throw new MatchAuthorError(
      `"${name}" holds key-value pairs, written as ${name}: { tags: { key: value } }.`,
    );
  }
  for (const key of Object.keys(side)) {
    if (!RESOURCE_GROUP_FILTER_FIELDS.includes(key)) {
      throw new MatchAuthorError(
        unknownFilterField(key, RESOURCE_GROUP_FILTER_FIELDS),
      );
    }
  }
}

/**
 * The `where` clauses one side contributes.
 *
 * @remarks
 * A comparison that resolves to `undefined` is a reference to a field the
 * filler has not filled in, so it drops rather than narrowing the list to
 * nothing.
 *
 * @param negate - Whether this is the `exclude` side.
 */
function clausesFor(
  side: ResourceGroupFilter | undefined,
  negate: boolean,
  readField: ReadField,
): readonly string[] {
  const clauses: string[] = [];

  if (side?.location !== undefined) {
    const match = resolveMatch(side.location, readField);
    if (match !== undefined) {
      clauses.push(`| where ${renderMatch('location', match, negate)}`);
    }
  }

  for (const [key, value] of Object.entries(side?.tags ?? {})) {
    const match = resolveMatch(value, readField);
    if (match === undefined) continue;
    const column = `tostring(tags[${kqlStringLiteral(key)}])`;
    clauses.push(`| where ${renderMatch(column, match, negate)}`);
  }

  return clauses;
}

/**
 * Whether the picker may fetch.
 *
 * @param subscriptionId - The subscription from the referenced field, or
 * `undefined` while that field is still empty.
 */
export function resourceGroupQueryEnabled(
  subscriptionId: string | undefined,
): boolean {
  return subscriptionId !== undefined && subscriptionId.length > 0;
}

/** Narrows an already-validated answer to what this picker guarantees. */
export function selectResourceGroups(
  answer: Answer,
): Selected<AzureResourceGroup> {
  return selectedFrom(answer, isAzureResourceGroup);
}

function isAzureResourceGroup(row: unknown): row is AzureResourceGroup {
  return isPickerValue<AzureResourceGroup>(row, RESOURCE_GROUP_FIELDS);
}
